import { useState } from 'react';
import { ChevronDown, ChevronUp, Image, FileText } from 'lucide-react';
import { Media } from '@/types';
import ImageLightbox from '@/components/ui/ImageLightbox';

interface QuestionStimuliProps {
  media: Media[];
}

function QuestionStimuli({ media }: QuestionStimuliProps) {
  const [isExpanded, setIsExpanded] = useState(true);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  if (!media || media.length === 0) return null;

  const images = media.filter((m) => m.type?.startsWith('image/'));
  const videos = media.filter((m) => m.type?.startsWith('video/'));
  const pdfs = media.filter((m) => m.type === 'application/pdf');
  const audio = media.filter((m) => m.type?.startsWith('audio/'));

  // TELV1-296 #5a: stimuli images used to open in a new tab via
  // `<a target="_blank">`, breaking flow whenever a researcher
  // wanted a closer look at the question stimulus. Switched to the
  // same ImageLightbox used by RespondentMediaGallery so clicking
  // opens an inline overlay with keyboard nav (←/→/Esc) and side-
  // by-side cycling when there are multiple stimuli.
  const lightboxImages = images.map((img) => ({
    url: img.url,
    caption: img.label || img.name,
  }));

  const openLightbox = (idx: number) => {
    setLightboxIndex(idx);
    setLightboxOpen(true);
  };

  return (
    <div className="mb-6">
      <button
        type="button"
        onClick={() => setIsExpanded(!isExpanded)}
        className="flex items-center gap-2 mb-3 text-sm font-medium text-gray-600 hover:text-gray-800 transition-colors"
      >
        <Image className="h-4 w-4" />
        <span>
          Stimuli ({media.length} {media.length === 1 ? 'item' : 'items'})
        </span>
        {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
      </button>

      {isExpanded && (
        <div className="flex flex-col gap-4">
          {/* Images Grid */}
          {images.length > 0 && (
            <div
              className={`grid gap-3 ${images.length === 1 ? 'grid-cols-1 max-w-md' : 'grid-cols-2 md:grid-cols-3'}`}
            >
              {images.map((img, idx) => (
                <div key={img._id} className="relative group">
                  <button
                    type="button"
                    onClick={() => openLightbox(idx)}
                    className="block w-full p-0 border-0 bg-transparent focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/30 rounded-lg"
                    aria-label={`Open ${img.label || img.name} in lightbox`}
                  >
                    <img
                      src={img.url}
                      alt={img.label || img.name}
                      className="w-full h-auto rounded-lg border border-gray-200 object-cover max-h-64 hover:opacity-90 transition-opacity cursor-pointer"
                    />
                  </button>
                  {img.label && <p className="mt-1 text-xs text-gray-500 truncate">{img.label}</p>}
                </div>
              ))}
            </div>
          )}

          {/* Videos */}
          {videos.map((vid) => (
            <div key={vid._id} className="max-w-lg">
              {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
              <video
                src={vid.url}
                controls
                className="w-full rounded-lg border border-gray-200"
                preload="metadata"
              />
              {vid.label && <p className="mt-1 text-xs text-gray-500">{vid.label}</p>}
            </div>
          ))}

          {/* Audio */}
          {audio.map((aud) => (
            <div key={aud._id} className="max-w-lg">
              {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
              <audio src={aud.url} controls className="w-full" preload="metadata" />
              {aud.label && <p className="mt-1 text-xs text-gray-500">{aud.label}</p>}
            </div>
          ))}

          {/* PDFs */}
          {pdfs.map((pdf) => (
            <a
              key={pdf._id}
              href={pdf.url}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 p-3 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors max-w-md"
            >
              <FileText className="h-5 w-5 text-gray-500 flex-shrink-0" />
              <span className="text-sm text-gray-700 truncate">{pdf.label || pdf.name}</span>
            </a>
          ))}
        </div>
      )}

      {lightboxImages.length > 0 && (
        <ImageLightbox
          images={lightboxImages}
          initialIndex={lightboxIndex}
          open={lightboxOpen}
          onOpenChange={setLightboxOpen}
        />
      )}
    </div>
  );
}

export default QuestionStimuli;
